/**
 * custom-search.js
 * Replaces Shopify's native predictive search with a custom overlay
 * that fetches results from the LivGood search API.
 *
 * Features:
 *  - Debounced live search while typing (≥ 2 chars)
 *  - Overlay shows up to 8 product cards matching the store card design
 *  - "Did you mean?" suggestion
 *  - Add to Cart / Remove from Cart via Shopify cart API
 *  - Press Enter or "See all" → /search?q=…
 *  - Full search-results page (/search) rendered by CustomSearchPage
 *    with sort dropdown and numbered pagination
 */

(function () {
  'use strict';

  /* ── Config ──────────────────────────────────────────────── */

  const API_URL    = 'https://livgood-pt.herokuapp.com/api/v2/search/products';
  const PAGE_LIMIT = 50;
  const DEBOUNCE_MS = 320;

  /* ── Utilities ───────────────────────────────────────────── */

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Format a price (in cents, as Shopify cart.js returns) using the store's
   * money format stored in window.KROWN.settings.shop_money_format.
   * Falls back to a simple £ prefix if the format is missing.
   */
  function formatMoney(cents) {
    const format =
      (window.KROWN && window.KROWN.settings && window.KROWN.settings.shop_money_format) ||
      '{{amount}}';
    const amount       = (cents / 100).toFixed(2);
    const amountNoDec  = Math.floor(cents / 100).toString();
    return format
      .replace(/\{\{amount\}\}/g,                             amount)
      .replace(/\{\{amount_no_decimals\}\}/g,                 amountNoDec)
      .replace(/\{\{amount_with_comma_separator\}\}/g,        amount.replace('.', ','))
      .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, amountNoDec);
  }

  /**
   * Our API returns prices as currency units (e.g. 0.70 = €0.70).
   * Convert to cents so formatMoney works uniformly.
   */
  function formatApiPrice(price) {
    const cents = Math.round(parseFloat(price || 0) * 100);
    return formatMoney(cents);
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Shopify Cart API ────────────────────────────────────── */

  async function cartFetch() {
    const res = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  async function cartAdd(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Add to cart failed');
    }
    return res.json();
  }

  async function cartChange(variantId, quantity = 0) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity }),
    });
    if (!res.ok) throw new Error('Cart change failed');
    return res.json();
  }

  /** Refresh the cart count + total shown in the header. */
  async function refreshHeaderCart() {
    const cart = await cartFetch();
    document.querySelectorAll('[data-header-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
    });
    document.querySelectorAll('[data-header-cart-total]').forEach(el => {
      el.textContent = formatMoney(cart.total_price);
    });
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return cart;
  }

  /* ── LivGood Search API ──────────────────────────────────── */

  /**
   * @param {string}   query
   * @param {number}   page
   * @param {string}   sort
   * @param {number}   limit
   * @param {Object}   filters  – e.g. { vendor: ['Naturalia'], product_type: ['Cooking Oils'] }
   */
  async function apiSearch(query, page = 1, sort = 'relevance', limit = PAGE_LIMIT, filters = {}) {
    const body = { query, q: query, page, sort, limit };

    if (filters.vendor && filters.vendor.length > 0) {
      body.vendor = filters.vendor;
    }
    if (filters.product_type && filters.product_type.length > 0) {
      body.product_type = filters.product_type[0]; // API expects single string
    }

    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Search API request failed');
    return res.json();
  }

  /* ── Card button HTML helpers ────────────────────────────── */

  const SVG_PLUS  = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`;
  const SVG_MINUS = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`;
  const SVG_SPIN  = `<svg viewBox="25 25 50 50"><circle cx="50" cy="50" r="20" stroke-width="4" fill="none" stroke="currentColor"/></svg>`;

  function circlePlus(variantId) {
    return `<button class="cs-circle-btn cs-add-btn" data-variant-id="${esc(variantId)}" aria-label="Add to cart">
      <span class="cs-circle-btn__icon" aria-hidden="true">${SVG_PLUS}</span>
      <span class="cs-circle-btn__loader" aria-hidden="true">${SVG_SPIN}</span>
    </button>`;
  }

  function circleMinus(variantId) {
    return `<button class="cs-circle-btn cs-circle-btn--outline cs-remove-btn" data-variant-id="${esc(variantId)}" aria-label="Remove from cart">
      <span class="cs-circle-btn__icon" aria-hidden="true">${SVG_MINUS}</span>
      <span class="cs-circle-btn__loader" aria-hidden="true">${SVG_SPIN}</span>
    </button>`;
  }

  function circlePair(variantId, qty) {
    return `<div class="cs-circle-pair">
      ${circleMinus(variantId)}
      <span class="cs-circle-qty">${qty}</span>
      ${circlePlus(variantId)}
    </div>`;
  }

  /* ── Product Card HTML ───────────────────────────────────── */

  function buildCard(product, cartMap) {
    const variant    = (product.variants && product.variants[0]) || {};
    const variantId  = variant.shopify_variant_id || null;
    const available  = variant.available_for_sale !== false;
    const price      = parseFloat(variant.price != null ? variant.price : product.price) || 0;
    const compareRaw = variant.compare_at_price != null ? variant.compare_at_price : product.compare_at_price;
    const compare    = parseFloat(compareRaw) || 0;
    const imageUrl   = product.images && product.images[0] ? product.images[0].url : '';
    const qty        = variantId ? (cartMap.get(String(variantId)) || 0) : 0;
    const inCart     = qty > 0;

    /* Discount calculations */
    const hasDiscount  = compare > 0 && compare > price;
    const discountPct  = hasDiscount ? Math.round((1 - price / compare) * 100) : 0;
    const discountBadge = (hasDiscount && discountPct > 0)
      ? `<span class="cs-badge cs-badge--discount">-${discountPct}%</span>`
      : '';

    /* Price display: stacked when on sale */
    const priceHtml = hasDiscount
      ? `<span class="cs-price-compare">${formatApiPrice(compare)}</span>
         <span class="cs-price-sale">${formatApiPrice(price)}</span>`
      : formatApiPrice(price);

    const priceClass = hasDiscount
      ? 'cs-product-card__price cs-product-card__price--on-sale'
      : 'cs-product-card__price';

    let circleBtn = '';
    if (available && variantId) {
      if (inCart) {
        circleBtn = circlePair(variantId, qty);
      } else {
        circleBtn = circlePlus(variantId);
      }
    } else if (!available) {
      circleBtn = `<span class="cs-sold-out-tag">Sold out</span>`;
    }

    return `
      <div class="cs-product-card" data-variant-id="${esc(variantId)}" data-product-id="${esc(product.shopify_id)}">
        <a href="/products/${esc(product.handle)}" class="cs-product-card__image">
          ${imageUrl
            ? `<img src="${esc(imageUrl)}" alt="${esc(product.title)}" loading="lazy" width="400" height="400">`
            : `<div class="cs-product-card__placeholder"><svg width="40" height="40" viewBox="0 0 40 40" fill="none"><path d="M8 32l8-8 5 5 7-9 8 12H8z" fill="currentColor" opacity=".2"/><circle cx="14" cy="14" r="4" fill="currentColor" opacity=".2"/></svg></div>`
          }
          ${discountBadge}
        </a>
        <div class="cs-product-card__body">
          ${product.vendor ? `<p class="cs-product-card__vendor">${esc(product.vendor)}</p>` : ''}
          <a class="cs-product-card__title" href="/products/${esc(product.handle)}" title="${esc(product.title)}">
            ${esc(product.title)}
          </a>
          <div class="cs-product-card__footer">
            <div class="${priceClass}">${priceHtml}</div>
            ${circleBtn}
          </div>
        </div>
      </div>`;
  }

  /* ── Cart Button Handlers (shared) ──────────────────────── */

  async function handleAddBtn(btn, cartMap) {
    const id = btn.dataset.variantId;
    if (!id) return;
    btn.classList.add('is-loading');
    try {
      await cartAdd(id);
      const newQty = (cartMap.get(id) || 0) + 1;
      cartMap.set(id, newQty);
      const pair = btn.closest('.cs-circle-pair');
      if (pair) {
        pair.querySelector('.cs-circle-qty').textContent = newQty;
        btn.classList.remove('is-loading');
      } else {
        btn.outerHTML = circlePair(id, newQty);
      }
      await refreshHeaderCart();
    } catch (err) {
      console.error('[CustomSearch] Add to cart error:', err);
      btn.classList.remove('is-loading');
    }
  }

  async function handleRemoveBtn(btn, cartMap) {
    const id = btn.dataset.variantId;
    if (!id) return;
    const currentQty = cartMap.get(id) || 1;
    const newQty     = currentQty - 1;
    btn.classList.add('is-loading');
    try {
      await cartChange(id, newQty);
      const pair = btn.closest('.cs-circle-pair');
      if (newQty <= 0) {
        cartMap.delete(id);
        if (pair) {
          pair.outerHTML = circlePlus(id);
        } else {
          btn.outerHTML = circlePlus(id);
        }
      } else {
        cartMap.set(id, newQty);
        if (pair) pair.querySelector('.cs-circle-qty').textContent = newQty;
        btn.classList.remove('is-loading');
      }
      await refreshHeaderCart();
    } catch (err) {
      console.error('[CustomSearch] Remove from cart error:', err);
      btn.classList.remove('is-loading');
    }
  }

  /* ── CustomSearchManager (header overlay) ────────────────── */

  class CustomSearchManager {
    constructor(inputs, overlay) {
      this.inputs       = inputs;
      this.overlay      = overlay;
      this.panel        = overlay.querySelector('.cs-overlay__panel');
      this.grid         = overlay.querySelector('.cs-overlay__grid');
      this.loadingEl    = overlay.querySelector('.cs-overlay__loading');
      this.headerEl     = overlay.querySelector('.cs-overlay__header-text');
      this.dymEl        = overlay.querySelector('.cs-overlay__did-you-mean');
      this.clearBtns    = inputs.map(i => i.closest('.cs-search-handle')?.querySelector('.cs-clear-btn')).filter(Boolean);
      this.currentQuery = '';
      this.cartMap      = new Map();

      this._debouncedSearch = debounce(this._doSearch.bind(this), DEBOUNCE_MS);
      this._attachEvents();
    }

    _syncClearBtns(hasText) {
      this.clearBtns.forEach(btn => {
        btn.style.display = hasText ? '' : 'none';
      });
    }

    _resetSearchState() {
      this.currentQuery = '';
      this.grid.innerHTML = '';
      if (this.headerEl) this.headerEl.innerHTML = '';
      if (this.dymEl) {
        this.dymEl.style.display = 'none';
        this.dymEl.innerHTML = '';
      }
      if (this.loadingEl) this.loadingEl.style.display = 'none';
      this.closeOverlay();
    }

    _attachEvents() {
      this.clearBtns.forEach(btn => {
        btn.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          this.inputs.forEach(inp => { inp.value = ''; });
          this._syncClearBtns(false);
          this._resetSearchState();
          const owner = btn.closest('.cs-search-handle')?.querySelector('.cs-search-input');
          if (owner) owner.focus();
        });
        btn.addEventListener('mousedown', e => e.preventDefault());
      });

      this.inputs.forEach(input => {
        input.addEventListener('input', e => {
          const q = e.target.value.trim();
          this.inputs.forEach(other => { if (other !== input) other.value = e.target.value; });
          this._syncClearBtns(e.target.value.length > 0);
          if (q.length >= 2) {
            this.currentQuery = q;
            this._debouncedSearch(q);
          } else {
            this.closeOverlay();
          }
        });

        input.addEventListener('keydown', e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const q = input.value.trim();
            if (q) this._navigate(q);
          }
          if (e.key === 'Escape') {
            this.inputs.forEach(inp => { inp.value = ''; });
            this._syncClearBtns(false);
            this._resetSearchState();
            input.blur();
          }
        });

        input.addEventListener('focus', () => {
          const q = input.value.trim();
          if (q.length >= 2 && this.grid.children.length > 0) {
            this._positionPanel();
            this.overlay.style.display = '';
          }
        });
      });

      this.grid.addEventListener('click', async e => {
        const addBtn    = e.target.closest('.cs-add-btn');
        const removeBtn = e.target.closest('.cs-remove-btn');
        if (addBtn)    await handleAddBtn(addBtn, this.cartMap);
        if (removeBtn) await handleRemoveBtn(removeBtn, this.cartMap);
      });

      document.addEventListener('click', e => {
        const wrappers = this.inputs.map(i => i.closest('[data-custom-search]') || i.parentElement);
        const inside   = wrappers.some(w => w.contains(e.target)) || this.overlay.contains(e.target);
        if (!inside) this.closeOverlay();
      }, true);

      const reposition = debounce(() => {
        if (this.overlay.style.display !== 'none') this._positionPanel();
      }, 100);
      window.addEventListener('scroll', reposition, { passive: true });
      window.addEventListener('resize', reposition, { passive: true });
    }

    _positionPanel() {
      const header = document.getElementById('site-header');
      if (!header) return;
      const rect   = header.getBoundingClientRect();
      const bottom = Math.max(0, rect.bottom);
      this.overlay.style.top = `${bottom}px`;
      this.panel.style.maxHeight = `calc(100vh - ${bottom}px)`;
    }

    async _doSearch(query) {
      this._showLoading();
      try {
        const [data, cart] = await Promise.all([
          apiSearch(query, 1),
          cartFetch(),
        ]);
        this.cartMap = new Map(cart.items.map(i => [String(i.variant_id), i.quantity]));
        this._renderResults(data, query);
      } catch (err) {
        console.error('[CustomSearch] Search error:', err);
        this.closeOverlay();
      }
    }

    _showLoading() {
      this.grid.innerHTML          = '';
      this.loadingEl.style.display = '';
      if (this.headerEl) this.headerEl.innerHTML = '';
      if (this.dymEl) this.dymEl.style.display = 'none';
      this._positionPanel();
      this.overlay.style.display = '';
    }

    _renderResults(data, query) {
      this.loadingEl.style.display = 'none';

      if (!data.products || data.products.length === 0) {
        this.grid.innerHTML = `<div class="cs-no-results">No results found for <strong>"${esc(query)}"</strong></div>`;
        if (this.headerEl) this.headerEl.innerHTML = `No results for &ldquo;<strong>${esc(query)}</strong>&rdquo;`;
        this.dymEl.style.display = 'none';
        return;
      }

      this.grid.innerHTML = data.products.map(p => buildCard(p, this.cartMap)).join('');

      if (this.headerEl) {
        const count = data.total;
        this.headerEl.innerHTML =
          `<span class="cs-result-count">${count.toLocaleString()}</span> ` +
          `result${count !== 1 ? 's' : ''} for &ldquo;<strong>${esc(query)}</strong>&rdquo;`;
      }

      const dym = data.did_you_mean;
      if (this.dymEl && dym && dym.toLowerCase() !== query.toLowerCase()) {
        this.dymEl.style.display = '';
        this.dymEl.innerHTML = `Did you mean: <a href="#" class="cs-dym-link" data-query="${esc(dym)}">${esc(dym)}</a>?`;
        this.dymEl.querySelector('.cs-dym-link').addEventListener('click', e => {
          e.preventDefault();
          const suggested = e.currentTarget.dataset.query;
          this.inputs.forEach(inp => (inp.value = suggested));
          this.currentQuery = suggested;
          this._doSearch(suggested);
        });
      } else if (this.dymEl) {
        this.dymEl.style.display = 'none';
      }
    }

    closeOverlay() {
      this.overlay.style.display = 'none';
    }

    _navigate(query) {
      window.location.href = `/search?q=${encodeURIComponent(query)}&type=product`;
    }
  }

  /* ── CustomSearchPage (full /search results page) ────────── */

  class CustomSearchPage {
    constructor(container) {
      this.container       = container;
      this.grid            = container.querySelector('.cs-page__grid');
      this.paginationEl    = container.querySelector('.cs-page__pagination');
      this.totalEl         = container.querySelector('.cs-page__total');
      this.dymEl           = container.querySelector('.cs-page__did-you-mean');
      this.queryInput      = container.querySelector('.cs-page__input');
      this.sortSelect      = container.querySelector('.cs-page__sort');
      this.filtersEl       = container.querySelector('.cs-page__filters');
      this.activeFiltersEl = container.querySelector('.cs-page__active-filters');
      this.cartMap         = new Map();
      this.page            = 1;
      this.total           = 0;
      this.totalPages      = 0;
      this.query           = '';
      this.sort            = 'relevance';
      this.limit           = PAGE_LIMIT;
      this.loading         = false;
      /* active filters: { vendor: string[], product_type: string[] } */
      this.activeFilters   = { vendor: [], product_type: [] };

      this._init();
    }

    async _init() {
      const params = new URLSearchParams(window.location.search);
      this.query = params.get('q') || '';
      this.sort  = params.get('sort') || 'relevance';
      this.page  = Math.max(1, parseInt(params.get('page') || '1', 10));

      /* Restore filters from URL */
      this.activeFilters.vendor       = params.getAll('vendor');
      this.activeFilters.product_type = params.getAll('product_type');

      if (this.queryInput) {
        this.queryInput.value = this.query;
        this.queryInput.addEventListener('keydown', e => {
          if (e.key === 'Enter') {
            const q = this.queryInput.value.trim();
            if (q) window.location.href = `/search?q=${encodeURIComponent(q)}&type=product`;
          }
        });
      }

      if (this.sortSelect) {
        this.sortSelect.value = this.sort;
        this.sortSelect.addEventListener('change', () => {
          this.sort = this.sortSelect.value;
          this._goToPage(1);
        });
      }

      this.grid.addEventListener('click', async e => {
        const addBtn    = e.target.closest('.cs-add-btn');
        const removeBtn = e.target.closest('.cs-remove-btn');
        if (addBtn)    await handleAddBtn(addBtn, this.cartMap);
        if (removeBtn) await handleRemoveBtn(removeBtn, this.cartMap);
      });

      if (this.query) {
        await this._fetchPage(this.page);
      } else {
        this._showEmpty('Enter a search term above to find products.');
      }
    }

    /* ── Filter helpers ────────────────────────────────────── */

    _hasFilters() {
      return this.activeFilters.vendor.length > 0 || this.activeFilters.product_type.length > 0;
    }

    /**
     * Toggle a filter value on/off.
     * product_type is single-select; vendor is multi-select.
     */
    _toggleFilter(type, value) {
      const list = this.activeFilters[type];
      if (!list) return;
      const idx = list.indexOf(value);
      if (type === 'product_type') {
        /* single-select: clicking active value clears it */
        this.activeFilters[type] = idx >= 0 ? [] : [value];
      } else {
        if (idx >= 0) {
          list.splice(idx, 1);
        } else {
          list.push(value);
        }
      }
      this._goToPage(1);
    }

    _clearAllFilters() {
      this.activeFilters = { vendor: [], product_type: [] };
      this._goToPage(1);
    }

    /* ── Rendering ─────────────────────────────────────────── */

    /**
     * Build the filters sidebar from API facets response.
     * facets: { product_types: [{value, count}], vendors: [{value, count}] }
     *
     * Structure:
     *  .cs-filters-mobile-bar   ← mobile-only toggle button
     *  .cs-filters-body         ← wraps all groups; hidden on mobile until toggled
     *    .cs-facet-group        ← one per facet type
     *      .cs-facet-group__toggle  ← clickable header with chevron
     *      .cs-facet-list-wrap  ← animated height wrapper
     *        ul.cs-facet-list
     */
    _renderFacets(facets) {
      if (!this.filtersEl) return;
      if (!facets || ((!facets.product_types || facets.product_types.length === 0) &&
                      (!facets.vendors       || facets.vendors.length === 0))) {
        this.filtersEl.innerHTML = '';
        return;
      }

      const groups = [
        { key: 'product_type', label: 'Product Type', items: facets.product_types || [] },
        { key: 'vendor',       label: 'Brand',        items: facets.vendors       || [] },
      ];

      const isMobile = window.innerWidth < 768;
      if (!this._groupOpen) this._groupOpen = {};

      /* Active filter count for the mobile badge */
      const activeCount = this.activeFilters.vendor.length + this.activeFilters.product_type.length;

      const SVG_CHEVRON = `<svg class="cs-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.5 5l4.5 4.5L11.5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const SVG_FILTER  = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4h12M5 8h6M7 12h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;

      /* Mobile toggle bar */
      let html = `<div class="cs-filters-mobile-bar">
        <button class="cs-filters-mobile-toggle" aria-expanded="${this._mobileFiltersOpen ? 'true' : 'false'}">
          ${SVG_FILTER}
          <span>Filters</span>
          ${activeCount > 0 ? `<span class="cs-filters-mobile-badge">${activeCount}</span>` : ''}
          ${SVG_CHEVRON}
        </button>
      </div>`;

      /* Collapsible body */
      html += `<div class="cs-filters-body${this._mobileFiltersOpen ? ' is-open' : ''}">`;

      groups.forEach(({ key, label, items }) => {
        if (!items.length) return;
        /* Default: open on desktop, collapsed on mobile */
        const isOpen = this._groupOpen[key] !== undefined
          ? this._groupOpen[key]
          : !isMobile;

        html += `<div class="cs-facet-group${isOpen ? '' : ' is-collapsed'}" data-group-key="${esc(key)}">
          <button class="cs-facet-group__toggle" aria-expanded="${isOpen}" aria-controls="cs-facet-list-${esc(key)}">
            <span class="cs-facet-group__toggle-label">${esc(label)}</span>
            ${activeCount > 0 && this.activeFilters[key].length > 0
              ? `<span class="cs-facet-group__badge">${this.activeFilters[key].length}</span>`
              : ''}
            ${SVG_CHEVRON}
          </button>
          <div class="cs-facet-list-wrap" id="cs-facet-list-${esc(key)}">
            <ul class="cs-facet-list" role="list">`;
        items.forEach(({ value, count }) => {
          const active = this.activeFilters[key].includes(value);
          html += `<li>
              <button
                class="cs-facet-item${active ? ' is-active' : ''}"
                data-filter-type="${esc(key)}"
                data-filter-value="${esc(value)}"
                aria-pressed="${active}"
              >
                <span class="cs-facet-item__label">${esc(value)}</span>
                <span class="cs-facet-item__count">${count}</span>
              </button>
            </li>`;
        });
        html += `    </ul>
          </div>
        </div>`;
      });

      html += `</div>`; /* end .cs-filters-body */
      this.filtersEl.innerHTML = html;

      /* Wire: mobile panel toggle */
      const mobileToggle = this.filtersEl.querySelector('.cs-filters-mobile-toggle');
      if (mobileToggle) {
        mobileToggle.addEventListener('click', () => {
          this._mobileFiltersOpen = !this._mobileFiltersOpen;
          const body = this.filtersEl.querySelector('.cs-filters-body');
          body.classList.toggle('is-open', this._mobileFiltersOpen);
          mobileToggle.setAttribute('aria-expanded', String(this._mobileFiltersOpen));
        });
      }

      /* Wire: per-group collapse toggles */
      this.filtersEl.querySelectorAll('.cs-facet-group__toggle').forEach(btn => {
        btn.addEventListener('click', () => {
          const group    = btn.closest('.cs-facet-group');
          const key      = group.dataset.groupKey;
          const collapse = !group.classList.contains('is-collapsed');
          group.classList.toggle('is-collapsed', collapse);
          btn.setAttribute('aria-expanded', String(!collapse));
          this._groupOpen[key] = !collapse;
        });
      });

      /* Wire: facet item clicks */
      this.filtersEl.querySelectorAll('.cs-facet-item').forEach(btn => {
        btn.addEventListener('click', () => {
          this._toggleFilter(btn.dataset.filterType, btn.dataset.filterValue);
        });
      });
    }

    /** Render active filter chips above the grid. */
    _renderActiveFilters() {
      if (!this.activeFiltersEl) return;
      if (!this._hasFilters()) {
        this.activeFiltersEl.style.display = 'none';
        this.activeFiltersEl.innerHTML = '';
        return;
      }

      const labelMap = { vendor: 'Brand', product_type: 'Type' };
      let html = '<div class="cs-active-filters">';

      ['product_type', 'vendor'].forEach(key => {
        this.activeFilters[key].forEach(value => {
          html += `<button class="cs-filter-chip" data-filter-type="${esc(key)}" data-filter-value="${esc(value)}" aria-label="Remove filter ${esc(value)}">
            <span class="cs-filter-chip__label">${esc(labelMap[key])}: ${esc(value)}</span>
            <span class="cs-filter-chip__remove" aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
            </span>
          </button>`;
        });
      });

      html += `<button class="cs-filter-chip cs-filter-chip--clear" aria-label="Clear all filters">Clear all</button>`;
      html += '</div>';

      this.activeFiltersEl.innerHTML = html;
      this.activeFiltersEl.style.display = '';

      this.activeFiltersEl.querySelectorAll('.cs-filter-chip[data-filter-type]').forEach(chip => {
        chip.addEventListener('click', () => {
          this._toggleFilter(chip.dataset.filterType, chip.dataset.filterValue);
        });
      });

      const clearBtn = this.activeFiltersEl.querySelector('.cs-filter-chip--clear');
      if (clearBtn) clearBtn.addEventListener('click', () => this._clearAllFilters());
    }

    /* ── Navigation & fetch ────────────────────────────────── */

    /** Navigate to a page: update URL, scroll up, fetch. */
    _goToPage(page) {
      this.page = page;
      const url = new URL(window.location.href);
      url.searchParams.set('q', this.query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('sort', this.sort);
      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }

      /* Sync filters to URL */
      url.searchParams.delete('vendor');
      url.searchParams.delete('product_type');
      this.activeFilters.vendor.forEach(v => url.searchParams.append('vendor', v));
      this.activeFilters.product_type.forEach(v => url.searchParams.append('product_type', v));

      history.pushState({}, '', url.toString());
      this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this._fetchPage(page);
    }

    async _fetchPage(page) {
      if (this.loading) return;
      this.loading = true;
      this.grid.innerHTML = `<div class="cs-page__loading" style="grid-column:1/-1;display:flex;align-items:center;justify-content:center;padding:5rem"><div class="cs-loading-spinner"></div></div>`;
      if (this.paginationEl) this.paginationEl.innerHTML = '';

      try {
        const [data, cart] = await Promise.all([
          apiSearch(this.query, page, this.sort, this.limit, this.activeFilters),
          cartFetch(),
        ]);
        this.cartMap    = new Map(cart.items.map(i => [String(i.variant_id), i.quantity]));
        this.page       = data.page || page;
        this.total      = data.total || 0;
        this.totalPages = data.total_pages || Math.ceil(this.total / this.limit) || 0;

        if (this.totalEl) {
          const count = this.total;
          this.totalEl.innerHTML =
            `<span class="cs-total-count">${count.toLocaleString()}</span> ` +
            `result${count !== 1 ? 's' : ''} for &ldquo;<strong>${esc(this.query)}</strong>&rdquo;`;
        }

        const dym = data.did_you_mean;
        if (this.dymEl && dym && dym.toLowerCase() !== this.query.toLowerCase()) {
          this.dymEl.style.display = '';
          this.dymEl.innerHTML = `Did you mean: <a href="/search?q=${encodeURIComponent(dym)}&type=product">${esc(dym)}</a>?`;
        } else if (this.dymEl) {
          this.dymEl.style.display = 'none';
        }

        /* Render facets + active chips before products */
        this._renderFacets(data.facets);
        this._renderActiveFilters();

        if (!data.products || data.products.length === 0) {
          this._showEmpty(`No results found for <strong>"${esc(this.query)}"</strong>.`);
          return;
        }

        this.grid.innerHTML = data.products.map(p => buildCard(p, this.cartMap)).join('');
        this._renderPagination();
      } catch (err) {
        console.error('[CustomSearchPage] Error:', err);
        this._showEmpty('An error occurred. Please try again.');
      } finally {
        this.loading = false;
      }
    }

    /* ── Pagination ────────────────────────────────────────── */

    /** Render numbered pagination controls. */
    _renderPagination() {
      if (!this.paginationEl) return;
      if (this.totalPages <= 1) {
        this.paginationEl.innerHTML = '';
        return;
      }

      const current = this.page;
      const total   = this.totalPages;
      const pages   = this._pageNumbers(current, total);

      const SVG_PREV = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const SVG_NEXT = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      let html = '<nav class="cs-pagination" aria-label="Search results pages"><ul class="cs-pagination__list">';

      html += `<li>
        <button class="cs-pagination__btn cs-pagination__btn--arrow"
          aria-label="Previous page"
          ${current <= 1 ? 'disabled aria-disabled="true"' : `data-page="${current - 1}"`}>
          ${SVG_PREV}
        </button>
      </li>`;

      pages.forEach(p => {
        if (p === '...') {
          html += `<li><span class="cs-pagination__ellipsis" aria-hidden="true">…</span></li>`;
        } else {
          const active = p === current;
          html += `<li>
            <button
              class="cs-pagination__btn cs-pagination__btn--num${active ? ' is-active' : ''}"
              ${active ? 'aria-current="page" disabled' : `data-page="${p}"`}
              aria-label="Page ${p}"
            >${p}</button>
          </li>`;
        }
      });

      html += `<li>
        <button class="cs-pagination__btn cs-pagination__btn--arrow"
          aria-label="Next page"
          ${current >= total ? 'disabled aria-disabled="true"' : `data-page="${current + 1}"`}>
          ${SVG_NEXT}
        </button>
      </li>`;

      html += '</ul></nav>';
      this.paginationEl.innerHTML = html;

      this.paginationEl.querySelectorAll('[data-page]').forEach(btn => {
        btn.addEventListener('click', () => {
          const p = parseInt(btn.dataset.page, 10);
          if (p && p !== this.page) this._goToPage(p);
        });
      });
    }

    /** Returns array of page numbers + '...' strings to display. */
    _pageNumbers(current, total) {
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const pages = [1];
      if (current > 3) pages.push('...');
      const start = Math.max(2, current - 1);
      const end   = Math.min(total - 1, current + 1);
      for (let p = start; p <= end; p++) pages.push(p);
      if (current < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    }

    _showEmpty(message) {
      this.grid.innerHTML = `
        <div class="cs-page__empty">
          <p class="cs-page__empty-title">${message}</p>
        </div>`;
      if (this.paginationEl) this.paginationEl.innerHTML = '';
    }
  }

  /* ── Bootstrap ───────────────────────────────────────────── */

  function init() {
    const overlay = document.getElementById('cs-search-overlay');
    const inputs  = Array.from(document.querySelectorAll('.cs-search-input'));

    if (overlay && inputs.length) {
      new CustomSearchManager(inputs, overlay);
    }

    const pageContainer = document.getElementById('cs-search-page');
    if (pageContainer) {
      new CustomSearchPage(pageContainer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
